// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import { Moves } from '@gql/base-types';

import { useCustomerConfig } from '@store';
import { getFormattedVehicleFromMove } from '@services/moveService';
import { formatPhone } from '@services/formatService';
import { useSearchResults } from '@features/searchResults/providers/SearchResultsProvider';

import { Typography } from '@mui/material';

import Table from '@components/TableComponents/Table';
import Column from '@components/TableComponents/Table';
import MoveIdBadge from '@components/Badges/MoveIdBadge';
import MoveTypeBadge from '@components/Badges/MoveTypeBadge';
import MoveStatusBadge from '@components/Badges/MoveStatusBadge';
import { movePath } from '@features/products/productRoutes';

// Map table column names to database field names
const columnToDbFieldMap: Record<string, string> = {
  market: 'customer_id', // Sort by customer ID (can't sort by nested customer.name directly)
  payer: 'payer_id',
  move_id: 'id',
  type: 'consumer_type',
  consumer: 'consumer_name',
  ref_num: 'reference_num',
  vin: 'vehicle_vin',
  vehicle: 'vehicle_year',
  time_requested: 'ready_by',
  time_completed: 'delivery_successful',
  status: 'status',
  lane: 'lane_id', // Sort by lane ID (can't sort by nested lane.description directly)
  created_by: 'createdBy',
};

// Reverse mapping: database field names to column names
const dbFieldToColumnMap: Record<string, string> = Object.entries(columnToDbFieldMap).reduce(
  (acc, [columnName, dbField]) => {
    acc[dbField] = columnName;
    return acc;
  },
  {} as Record<string, string>
);

const formatTime = (time: string) => {
  if (time && typeof time === `string`) {
    let newTime = dayjs(time).format(`M/D/YYYY h:mm A z`);
    newTime = newTime.replace(/\s/g, '\xa0');
    return newTime;
  }
  return null;
};

const statusOrder = [
  ``,
  `dispatched`,
  `pickup started`,
  `pickup arrived`,
  `pickup successful`,
  `delivery started`,
  `delivery arrived`,
  `delivery successful`,
];

// TYPES ---------------------------------------------------------------- //

interface SearchResultsTableProps {
  moves: Moves[];
  moveCount: number;
  isAdmin: boolean;
  // Only the count is read, to decide whether the payer column shows.
  payersArray: unknown[];
}

// COMPONENT ---------------------------------------------------------------- //

const SearchResultsTable = ({ moves, moveCount, isAdmin, payersArray }: SearchResultsTableProps) => {
  const navigate = useNavigate();
  const { order, orderBy, setOrder, setOrderBy, setOffset } = useSearchResults();

  const customerConfig = useCustomerConfig();
  const refLabel = customerConfig?.reference_num_label || 'Reference ID';

  const [presentMoveTypes, setPresentMoveTypes] = React.useState<string>(`none`);

  // Convert DB field name to column name for Table component
  const tableOrderBy = dbFieldToColumnMap[orderBy] || orderBy;

  // Handle sort changes from Table component
  const handleOrderChange = (newOrder: 'asc' | 'desc' | false) => {
    if (newOrder === false) return; // Skip if false
    setOrder(newOrder);
    setOffset(0); // Reset to first page when sorting changes
  };

  const handleOrderByChange = (columnName: string) => {
    // Map column name to database field name
    const dbField = columnToDbFieldMap[columnName] || columnName;
    setOrderBy(dbField);
    setOffset(0); // Reset to first page when sorting changes
  };

  React.useEffect(() => {
    // Determine the present move types
    let newMoveTypes = moves.map(move => (move?.consumer_type ? `concierge` : `operational`));
    newMoveTypes = [...new Set(newMoveTypes)];

    if (newMoveTypes.length === 1) {
      setPresentMoveTypes(newMoveTypes[0]);
    } else {
      setPresentMoveTypes(`all`);
    }
  }, [moves]);

  const getType = (move: Moves) => {
    const { consumer_type } = move;

    if (consumer_type === 'customer' && move?.consumer_at_pickup === 1) return 'Pickup';
    if (consumer_type === 'customer' && move?.consumer_at_pickup === 0) return 'Return';
    if (consumer_type === 'loaner') return 'Loaner';
    return 'Operational';
  };

  const getStatusOrder = (move: Moves) => {
    const { cancel_status, status } = move;
    if (cancel_status) return `0-${move?.ready_by || move?.id}`;
    return `${statusOrder.indexOf(status || ``) + 1}-${move?.ready_by || move?.id}`;
  };

  return (
    <Table
      data={moves}
      order={order}
      orderBy={tableOrderBy}
      onOrderChange={handleOrderChange}
      onOrderByChange={handleOrderByChange}
      tableAriaLabel='moves'
      stickyHeader={true}
      rowHref={row => movePath('id' in row ? (row as Moves).id : undefined)}
      usePagination={true}
      hidePagination={true}
      totalRecordsCount={moveCount}
    >
      {isAdmin ? (
        <Column
          name='market'
          value={(move: Moves) => move?.customer?.name}
          renderer={(move: Moves) => move?.customer?.name || '-'}
          label='Market'
          style={{ minWidth: 192 }}
        />
      ) : null}

      {payersArray && payersArray.length > 0 ? (
        <Column
          name='payer'
          value={(move: Moves) => move?.payer_id || move?.customer?.name}
          renderer={(move: Moves) => move?.payer_id || move?.customer?.name || '-'}
          label='Payer'
          style={{ minWidth: 192 }}
        />
      ) : null}

      <Column
        name='move_id'
        value={(move: Moves) => move?.id}
        renderer={(move: Moves) => <MoveIdBadge move={move} />}
        label='Move ID'
        tooltip={`HopDrive's internal ID`}
        style={{ minWidth: 104 }}
      />

      {presentMoveTypes !== `operational` ? (
        <Column
          name='type'
          value={(move: Moves) => getType(move)}
          renderer={(move: Moves) => <MoveTypeBadge move={move} />}
          label='Type'
          style={{ minWidth: 96 }}
        />
      ) : null}

      {presentMoveTypes !== `operational` ? (
        <Column
          name='consumer'
          value={(move: Moves) =>
            move?.consumer_name || move?.consumer_phone
              ? `${move?.consumer_name} ${formatPhone(move?.consumer_phone)}`
              : undefined
          }
          renderer={(move: Moves) =>
            move?.consumer_name || move?.consumer_phone ? (
              <div>
                {move?.consumer_name && (
                  <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                    {move?.consumer_name}
                  </Typography>
                )}
                {move?.consumer_phone && (
                  <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                    {formatPhone(move?.consumer_phone)}
                  </Typography>
                )}
              </div>
            ) : (
              `-`
            )
          }
          label='Customer'
          style={{ minWidth: 144 }}
        />
      ) : null}

      <Column
        name='ref_num'
        value={(move: Moves) => move?.reference_num}
        renderer={(move: Moves) => move?.reference_num || '-'}
        label={refLabel}
        tooltip={`Your internal reference ID - Currently configured as ${refLabel}.`}
        style={{ whiteSpace: 'nowrap' }}
      />

      <Column
        name='vin'
        value={(move: Moves) => move?.vehicle_vin}
        renderer={(move: Moves) => move?.vehicle_vin || '-'}
        label='VIN'
        style={{ minWidth: 96 }}
      />

      <Column
        name='vehicle'
        value={(move: Moves) => getFormattedVehicleFromMove(move, true, false)}
        label='Vehicle'
        style={{ minWidth: 192 }}
      />

      <Column
        name='time_requested'
        value={(move: Moves) => move?.ready_by}
        renderer={(move: Moves) => formatTime(move?.ready_by || '') || '-'}
        label='Time Requested'
        style={{ minWidth: 144 }}
      />

      <Column
        name='time_completed'
        value={(move: Moves) => move?.delivery_successful}
        renderer={(move: Moves) => formatTime(move?.delivery_successful || '') || '-'}
        label='Time Completed'
        style={{ minWidth: 144 }}
      />

      <Column
        name='status'
        value={(move: Moves) => getStatusOrder(move)}
        renderer={(move: Moves) => <MoveStatusBadge move={move} />}
        label='Status'
        style={{ minWidth: 120 }}
      />

      <Column
        name='lane'
        value={(move: Moves) =>
          move?.lane?.pickup?.name && move?.lane?.delivery?.name
            ? `${move?.lane?.pickup?.name} to ${move?.lane?.delivery?.name}`
            : move?.lane?.description
        }
        renderer={(move: Moves) => (
          <div>
            {move?.lane?.pickup?.name && move?.lane?.delivery?.name ? (
              <>
                <Typography variant='body2'>{move?.lane?.pickup?.name}</Typography>
                <Typography variant='body2'>to {move?.lane?.delivery?.name}</Typography>
              </>
            ) : (
              <Typography variant='body2'>{move?.lane?.description || `-`}</Typography>
            )}
          </div>
        )}
        label='Lane'
        style={{ minWidth: 192 }}
      />

      <Column
        name='created_by'
        value={(move: Moves) => (move?.createdBy && move?.createdBy?.includes(`@`) ? move?.createdBy : undefined)}
        renderer={(move: Moves) => (move?.createdBy && move?.createdBy?.includes(`@`) ? move?.createdBy : '-')}
        label='Created By'
        style={{ minWidth: 120 }}
      />
    </Table>
  );
};

// EXPORT ---------------------------------------------------------------- //

// Memoize the component to prevent unnecessary re-renders
// Only re-render when moves data or other props change
export default React.memo(SearchResultsTable, (prevProps, nextProps) => {
  // Check if moves array has actually changed
  const movesEqual =
    prevProps.moves.length === nextProps.moves.length &&
    prevProps.moves.every((_, index) => {
      const prevMove = prevProps.moves[index];
      const nextMove = nextProps.moves[index];
      // Compare key fields that would affect the table display
      return (
        prevMove?.id === nextMove?.id &&
        prevMove?.status === nextMove?.status &&
        prevMove?.cancel_status === nextMove?.cancel_status &&
        prevMove?.consumer_pickup === nextMove?.consumer_pickup
      );
    });

  // Check if other props have changed
  const otherPropsEqual =
    prevProps.isAdmin === nextProps.isAdmin &&
    prevProps.moveCount === nextProps.moveCount &&
    JSON.stringify(prevProps.payersArray) === JSON.stringify(nextProps.payersArray);

  // Return true if props are equal (skip re-render), false if different (re-render)
  return movesEqual && otherPropsEqual;
});
